<template>
    <div :class="['pricing-workspace', { dark: isDark }]" data-test="pricing-workspace">
        <div v-if="!canViewPricing" class="permission-message" role="alert">
            You do not have permission to view product pricing.
        </div>

        <template v-else>
            <div v-if="loading" class="loading-message" role="status" aria-live="polite">
                Loading pricing…
            </div>

            <div v-else>
                <div class="workspace-toolbar">
                    <div class="workspace-heading">
                        <div class="eyebrow">{{ scopeLabel }}</div>
                        <div class="method-title-row">
                            <h3>{{ currentMethodLabel }}</h3>
                            <span
                                v-if="isInheritedShelf"
                                class="source-badge inherited"
                                data-test="inherited-shelf-badge"
                            >
                                Inherited from master
                            </span>
                            <span v-else-if="explicitItemPolicy" class="source-badge">
                                Explicit {{ isVariation ? "variation" : "product" }} policy
                            </span>
                        </div>
                        <p class="workspace-subtitle">{{ pricingSourceDescription }}</p>
                    </div>

                    <button
                        v-if="canEditPricing && !editMode"
                        type="button"
                        class="secondary-action"
                        data-test="edit-pricing"
                        @click="startEditing()"
                    >
                        Edit pricing
                    </button>
                </div>

                <div v-if="contextError" class="context-warning" role="alert" data-test="context-warning">
                    {{ contextError }}
                </div>
                <div v-if="legacyWarning" class="context-warning" role="alert" data-test="legacy-warning">
                    {{ legacyWarning }}
                </div>

                <div
                    v-if="isInheritedShelf"
                    class="inheritance-panel"
                    data-test="inherited-shelf-panel"
                >
                    <div>
                        <strong>{{ masterName }} controls this Shelf price.</strong>
                        <p>
                            Changes to the live master Shelf price flow to every variation without an
                            explicit Standard or Tiered override.
                        </p>
                    </div>
                    <div v-if="canEditPricing && !editMode" class="inline-actions">
                        <button
                            type="button"
                            class="text-action"
                            data-test="create-standard-override"
                            @click="startEditing('STANDARD')"
                        >
                            Create Standard override
                        </button>
                        <button
                            type="button"
                            class="text-action"
                            data-test="create-tiered-override"
                            @click="startEditing('TIERED')"
                        >
                            Create Tiered override
                        </button>
                        <button
                            v-if="canDisableShelfAtScope"
                            type="button"
                            class="danger-text-action"
                            data-test="disable-shelf"
                            :disabled="processing"
                            @click="disableShelf"
                        >
                            Disable master Shelf
                        </button>
                        <span v-else class="scope-action-note">
                            Switch to the Shelf policy's own scope to disable it.
                        </span>
                    </div>
                </div>

                <form v-if="editMode" class="policy-form" novalidate @submit.prevent="handleSavePrice">
                    <fieldset class="method-fieldset">
                        <legend>Pricing method</legend>
                        <p class="section-help">
                            Choose the base pricing rule for this {{ isVariation ? "variation" : "product" }}
                            at the selected scope.
                        </p>

                        <div class="method-grid" data-test="pricing-methods">
                            <label
                                v-for="method in pricingMethods"
                                :key="method.value"
                                :class="[
                                    'method-option',
                                    { selected: pricingMethod === method.value },
                                    { unavailable: isMethodUnavailable(method.value) },
                                ]"
                            >
                                <input
                                    v-model="pricingMethod"
                                    type="radio"
                                    name="pricing-method"
                                    :value="method.value"
                                    :disabled="isMethodUnavailable(method.value)"
                                    :data-test="`pricing-method-${method.value.toLowerCase()}`"
                                    @change="selectMethod(method.value)"
                                />
                                <span class="method-copy">
                                    <strong>{{ method.label }}</strong>
                                    <small>{{ method.description }}</small>
                                </span>
                            </label>
                        </div>

                        <p
                            v-if="isVariation && !masterShelfActive"
                            class="section-help shelf-unavailable-help"
                        >
                            Shelf becomes available here after it is enabled on {{ masterName }}.
                        </p>
                    </fieldset>

                    <section v-if="pricingMethod === 'STANDARD'" class="editor-section" data-test="standard-editor">
                        <div class="section-heading">
                            <div>
                                <h4>Standard pricing</h4>
                                <p>Set the regular unit price and keep the existing dated sale-price schedule.</p>
                            </div>
                        </div>

                        <div class="field-control regular-price-control">
                            <label for="regular-price">Regular unit price ($)</label>
                            <input
                                id="regular-price"
                                v-model="regularPrice"
                                type="number"
                                min="0"
                                step="0.01"
                                inputmode="decimal"
                                data-test="regular-price"
                            />
                        </div>

                        <div class="sale-price-heading">
                            <div>
                                <h5>Dated sale prices</h5>
                                <p>Sale prices remain independent of the new base-policy selection.</p>
                            </div>
                            <button type="button" class="text-action" data-test="add-sale-price" @click="addSalePrice">
                                <i class="fas fa-plus" aria-hidden="true"></i>
                                Add sale price
                            </button>
                        </div>

                        <div v-if="salePrices.length" class="repeatable-list">
                            <div
                                v-for="(salePrice, index) in salePrices"
                                :key="`sale-${index}`"
                                class="sale-price-row"
                            >
                                <div class="field-control">
                                    <label :for="`sale-price-${index}`">Sale price ($)</label>
                                    <input
                                        :id="`sale-price-${index}`"
                                        v-model="salePrice.price_list_rate"
                                        type="number"
                                        min="0"
                                        step="0.01"
                                        inputmode="decimal"
                                    />
                                </div>
                                <div class="field-control">
                                    <label :for="`sale-start-${index}`">Start date</label>
                                    <input
                                        :id="`sale-start-${index}`"
                                        v-model="salePrice.valid_from"
                                        type="date"
                                    />
                                </div>
                                <div class="field-control">
                                    <label :for="`sale-end-${index}`">End date</label>
                                    <input
                                        :id="`sale-end-${index}`"
                                        v-model="salePrice.valid_upto"
                                        type="date"
                                    />
                                </div>
                                <button
                                    type="button"
                                    class="icon-action"
                                    :aria-label="`Remove sale price ${index + 1}`"
                                    @click="removeSalePrice(index)"
                                >
                                    <i class="fas fa-times" aria-hidden="true"></i>
                                </button>
                            </div>
                        </div>
                    </section>

                    <section v-else-if="pricingMethod === 'TIERED'" class="editor-section" data-test="tiered-editor">
                        <div class="section-heading">
                            <div>
                                <h4>Tiered pricing</h4>
                                <p>
                                    The highest reached minimum-quantity breakpoint applies to all units. Quantity
                                    is totalled across package and batch lines for the same SKU.
                                </p>
                            </div>
                            <button type="button" class="text-action" data-test="add-tier" @click="addTier">
                                <i class="fas fa-plus" aria-hidden="true"></i>
                                Add breakpoint
                            </button>
                        </div>

                        <div class="repeatable-list" data-test="tier-rows">
                            <div v-for="(tier, index) in tiers" :key="`tier-${index}`" class="tier-row">
                                <div class="tier-index" aria-hidden="true">{{ index + 1 }}</div>
                                <div class="field-control">
                                    <label :for="`tier-min-${index}`">Minimum quantity</label>
                                    <input
                                        :id="`tier-min-${index}`"
                                        v-model="tier.min_quantity"
                                        type="number"
                                        min="1"
                                        step="1"
                                        inputmode="numeric"
                                        :disabled="index === 0"
                                    />
                                </div>
                                <div class="field-control">
                                    <label :for="`tier-price-${index}`">Unit price ($)</label>
                                    <input
                                        :id="`tier-price-${index}`"
                                        v-model="tier.unit_price"
                                        type="number"
                                        min="0.01"
                                        step="0.01"
                                        inputmode="decimal"
                                    />
                                </div>
                                <button
                                    type="button"
                                    class="icon-action"
                                    :disabled="tiers.length === 1"
                                    :aria-label="`Remove breakpoint ${index + 1}`"
                                    @click="removeTier(index)"
                                >
                                    <i class="fas fa-times" aria-hidden="true"></i>
                                </button>
                            </div>
                        </div>

                        <ul v-if="validationErrors.tiers.length" class="validation-list" role="alert">
                            <li v-for="error in validationErrors.tiers" :key="error">{{ error }}</li>
                        </ul>

                        <div v-if="tierSummaries.length" class="tier-summary" data-test="tier-summary">
                            <strong>Price ranges</strong>
                            <div class="tier-summary-list">
                                <span v-for="summary in tierSummaries" :key="summary.range">
                                    {{ summary.range }} · {{ formatPrice(summary.unitPrice) }}
                                </span>
                            </div>
                        </div>
                    </section>

                    <section v-else class="editor-section" data-test="shelf-editor">
                        <div class="section-heading">
                            <div>
                                <h4>Shelf pricing</h4>
                                <p>
                                    One live price is owned by {{ masterName }} and inherited by variations unless
                                    a variation has an explicit Standard or Tiered override.
                                </p>
                            </div>
                        </div>

                        <div v-if="isVariation" class="shelf-impact-panel">
                            <strong>Use {{ masterName }} Shelf price</strong>
                            <p>
                                Saving removes only this variation's explicit override. It does not change the
                                master Shelf price or another variation's override.
                            </p>
                            <div v-if="masterShelfActive" class="impact-price">
                                Current master price: {{ formatPrice(masterShelfPrice) }}
                            </div>
                        </div>
                        <div v-else>
                            <div class="field-control shelf-price-control">
                                <label for="shelf-price">Live Shelf unit price ($)</label>
                                <input
                                    id="shelf-price"
                                    v-model="fixedUnitPrice"
                                    type="number"
                                    min="0"
                                    step="0.01"
                                    inputmode="decimal"
                                    data-test="shelf-price"
                                />
                            </div>
                            <div class="shelf-impact-panel warning">
                                <strong>Live inheritance impact</strong>
                                <p v-if="affectedVariationCount !== null">
                                    Saving can update up to {{ affectedVariationCount }}
                                    {{ affectedVariationCount === 1 ? "variation" : "variations" }} without an
                                    explicit override.
                                </p>
                                <p v-else>
                                    Saving updates every variation without an explicit Standard or Tiered override.
                                </p>
                                <ul v-if="affectedVariations.length" class="variation-list">
                                    <li v-for="variation in affectedVariations" :key="variation.id || variation.item_code">
                                        {{ variation.name || variation.item_name || variation.item_code }}
                                    </li>
                                </ul>
                            </div>
                        </div>
                    </section>

                    <section class="editor-section customer-group-section" data-test="customer-group-section">
                        <div class="section-heading">
                            <div>
                                <h4>Customer-group location prices</h4>
                                <p>
                                    Each amount is an absolute base unit price for this location. It replaces the
                                    group's old global baseline; ordinary promotions are evaluated afterward.
                                </p>
                            </div>
                            <button
                                v-if="isLocationScope"
                                type="button"
                                class="text-action"
                                data-test="add-customer-group-price"
                                :disabled="!hasUnusedCustomerGroup"
                                @click="addCustomerGroupPrice"
                            >
                                <i class="fas fa-plus" aria-hidden="true"></i>
                                Add group price
                            </button>
                        </div>

                        <div v-if="!isLocationScope" class="scope-note" data-test="company-group-price-note">
                            Customer-group base prices are location-only. Select a warehouse to configure them.
                        </div>
                        <div v-else-if="customerGroupPrices.length" class="repeatable-list">
                            <div
                                v-for="(groupPrice, index) in customerGroupPrices"
                                :key="`group-${index}`"
                                class="customer-group-row"
                            >
                                <div class="field-control">
                                    <label :for="`customer-group-${index}`">Customer group</label>
                                    <select
                                        :id="`customer-group-${index}`"
                                        v-model="groupPrice.customer_group_id"
                                        :data-test="`customer-group-select-${index}`"
                                    >
                                        <option value="" disabled>Select a customer group</option>
                                        <option
                                            v-for="group in customerGroupOptions(groupPrice)"
                                            :key="group.id"
                                            :value="group.id"
                                            :disabled="isCustomerGroupUsed(group.id, index)"
                                        >
                                            {{ customerGroupOptionLabel(group) }}
                                        </option>
                                    </select>
                                </div>
                                <div class="field-control">
                                    <label :for="`customer-group-price-${index}`">Absolute unit price ($)</label>
                                    <input
                                        :id="`customer-group-price-${index}`"
                                        v-model="groupPrice.unit_price"
                                        type="number"
                                        min="0.01"
                                        step="0.01"
                                        inputmode="decimal"
                                    />
                                </div>
                                <button
                                    type="button"
                                    class="icon-action"
                                    :aria-label="`Remove ${groupPriceName(groupPrice)} price`"
                                    @click="removeCustomerGroupPrice(index)"
                                >
                                    <i class="fas fa-times" aria-hidden="true"></i>
                                </button>
                            </div>
                        </div>
                        <div v-else-if="isLocationScope" class="empty-note">
                            No customer-group prices at this location.
                        </div>

                        <ul
                            v-if="validationErrors.customerGroups.length"
                            class="validation-list"
                            role="alert"
                            data-test="customer-group-errors"
                        >
                            <li v-for="error in validationErrors.customerGroups" :key="error">{{ error }}</li>
                        </ul>
                    </section>

                    <div v-if="validationErrors.general" class="form-error" role="alert" data-test="pricing-error">
                        {{ validationErrors.general }}
                    </div>

                    <div class="form-actions">
                        <button type="button" class="secondary-action" :disabled="processing" @click="cancelEditing">
                            Cancel
                        </button>
                        <button type="submit" class="primary-action" :disabled="processing" data-test="save-pricing">
                            {{ processing ? "Saving…" : "Save pricing" }}
                        </button>
                    </div>
                </form>

                <div v-else class="policy-summary" data-test="pricing-summary">
                    <dl class="summary-grid">
                        <div>
                            <dt>Effective method</dt>
                            <dd>{{ currentMethodLabel }}</dd>
                        </div>
                        <div v-if="effectiveFixedPrice !== null">
                            <dt>Base unit price</dt>
                            <dd>{{ formatPrice(effectiveFixedPrice) }}</dd>
                        </div>
                        <div v-if="item_id && latestCostPrice !== null">
                            <dt>Last cost price</dt>
                            <dd>{{ formatPrice(latestCostPrice) }}</dd>
                        </div>
                    </dl>

                    <div v-if="effectiveMethod === 'TIERED' && tierSummaries.length" class="summary-section">
                        <h4>Tier breakpoints</h4>
                        <div class="tier-summary-list">
                            <span v-for="summary in tierSummaries" :key="summary.range">
                                {{ summary.range }} · {{ formatPrice(summary.unitPrice) }}
                            </span>
                        </div>
                    </div>

                    <div v-if="validSalePrices.length" class="summary-section">
                        <h4>Dated sale prices</h4>
                        <div class="responsive-table-wrap">
                            <table>
                                <thead>
                                    <tr>
                                        <th scope="col">Price</th>
                                        <th scope="col">Starts</th>
                                        <th scope="col">Ends</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr v-for="(salePrice, index) in validSalePrices" :key="`saved-sale-${index}`">
                                        <td>{{ formatPrice(salePrice.price_list_rate) }}</td>
                                        <td>{{ salePrice.valid_from }}</td>
                                        <td>{{ salePrice.valid_upto }}</td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                    </div>

                    <div v-if="isLocationScope" class="summary-section">
                        <h4>Customer-group location prices</h4>
                        <p class="section-help">Absolute base prices; ordinary promotions run afterward.</p>
                        <ul v-if="customerGroupPrices.length" class="group-price-summary">
                            <li v-for="groupPrice in customerGroupPrices" :key="groupPrice.customer_group_id">
                                <span>{{ groupPriceName(groupPrice) }}</span>
                                <strong>{{ formatPrice(groupPrice.unit_price) }}</strong>
                            </li>
                        </ul>
                        <div v-else class="empty-note">No customer-group prices at this location.</div>
                    </div>

                    <div v-if="canEditPricing && isVariation && selectedItemPolicy" class="summary-actions">
                        <button
                            type="button"
                            class="danger-text-action"
                            data-test="remove-override"
                            :disabled="processing"
                            @click="removeExplicitOverride"
                        >
                            Remove variation override
                        </button>
                    </div>
                    <div
                        v-if="canEditPricing && !isVariation && canDisableShelfAtScope"
                        class="summary-actions"
                    >
                        <button
                            type="button"
                            class="danger-text-action"
                            data-test="disable-master-shelf"
                            :disabled="processing"
                            @click="disableShelf"
                        >
                            Disable master Shelf
                        </button>
                    </div>
                </div>
            </div>
        </template>

        <div class="detail-link" v-if="item_id">
            <router-link :to="`/products/pricing/${item_id}`">View location-level pricing</router-link>
        </div>
    </div>
</template>

<script>
import { errMsg } from "../../utils/common"
import { http } from "../../utils/http"
import { PERMISSION_CODES, checkHasPermission } from "../../store/permission"

const PRICING_METHODS = [
    {
        value: "STANDARD",
        label: "Standard",
        description: "One regular unit price with optional dated sale prices.",
    },
    {
        value: "TIERED",
        label: "Tiered",
        description: "All units use the highest reached quantity breakpoint.",
    },
    {
        value: "SHELF",
        label: "Shelf",
        description: "A live master-product price inherited by its variations.",
    },
]

const emptyValidationErrors = () => ({
    general: "",
    tiers: [],
    customerGroups: [],
})

const hasValue = (value) => value !== null && value !== undefined && value !== ""
const finiteNumber = (value) => {
    if (!hasValue(value)) return null
    const number = Number(value)
    return Number.isFinite(number) ? number : null
}

export default {
    name: "PricingForm",
    props: {
        item_id: {
            type: String,
            default: null,
        },
        item_code: {
            type: String,
            default: null,
        },
        warehouse: {
            type: String,
            default: null,
        },
        variations: {
            type: Array,
            default: () => [],
        },
    },
    data() {
        return {
            pricingMethods: PRICING_METHODS,
            pricingMethod: "STANDARD",
            regularPrice: 0,
            fixedUnitPrice: 0,
            latestCostPrice: null,
            salePrices: [],
            tiers: [{ min_quantity: 1, unit_price: "" }],
            customerGroupPrices: [],
            customerGroups: [],
            policyContext: null,
            explicitItemPolicy: null,
            masterShelfPolicy: null,
            effectivePolicy: null,
            processing: false,
            editMode: false,
            loading: false,
            contextError: "",
            legacyWarning: "",
            legacyPricesLoaded: false,
            validationErrors: emptyValidationErrors(),
            pricingRequestGeneration: 0,
        }
    },
    computed: {
        currentUser() {
            return this.$store && this.$store.state.auth ? this.$store.state.auth.user : null
        },
        miscState() {
            return this.$store && this.$store.state.misc ? this.$store.state.misc : {}
        },
        isDark() {
            return Boolean(
                this.miscState.THEMES && this.miscState.theme === this.miscState.THEMES.DARK
            )
        },
        canViewPricing() {
            return checkHasPermission({
                isAdmin: Boolean(this.currentUser && this.currentUser.isAdmin),
                permissionId: PERMISSION_CODES.CMS_VIEW_PRODUCT,
                userPermissionIds: (this.currentUser && this.currentUser.permissionIds) || [],
            })
        },
        canEditPricing() {
            return checkHasPermission({
                isAdmin: Boolean(this.currentUser && this.currentUser.isAdmin),
                permissionId: PERMISSION_CODES.CMS_EDIT_PRODUCT,
                userPermissionIds: (this.currentUser && this.currentUser.permissionIds) || [],
            })
        },
        isLocationScope() {
            return this.warehouse !== null && this.warehouse !== ""
        },
        scopeLabel() {
            return this.isLocationScope ? "Location pricing" : "Company pricing"
        },
        contextItem() {
            return (this.policyContext && this.policyContext.item) || {}
        },
        contextMaster() {
            return (this.policyContext && this.policyContext.master) || {}
        },
        isVariation() {
            const inheritance = (this.policyContext && this.policyContext.inheritance) || {}
            return Boolean(
                this.contextItem.parent ||
                    this.contextItem.parent_id ||
                    this.contextItem.is_variation ||
                    inheritance.is_variation
            )
        },
        masterName() {
            return (
                this.contextMaster.name ||
                this.contextMaster.item_name ||
                this.contextMaster.item_code ||
                "the master product"
            )
        },
        effectiveMethod() {
            return this.normalizeMethod(this.effectivePolicy && this.effectivePolicy.method)
        },
        currentMethodLabel() {
            const method = this.pricingMethods.find((item) => item.value === this.effectiveMethod)
            return method ? `${method.label} pricing` : "Pricing not configured"
        },
        masterShelfActive() {
            return Boolean(
                this.masterShelfPolicy &&
                    this.normalizeMethod(this.masterShelfPolicy.method) === "SHELF" &&
                    this.masterShelfPolicy.active !== false &&
                    this.masterShelfPolicy.enabled !== false
            )
        },
        masterShelfPrice() {
            return this.policyFixedPrice(this.masterShelfPolicy)
        },
        selectedItemPolicy() {
            const scopes = (this.policyContext && this.policyContext.explicit_item_policy_scopes) || {}
            return this.isLocationScope ? scopes.warehouse || null : scopes.company || null
        },
        selectedMasterShelf() {
            const scopes = (this.policyContext && this.policyContext.master_shelf_scopes) || {}
            return this.isLocationScope ? scopes.warehouse || null : scopes.company || null
        },
        canDisableShelfAtScope() {
            return Boolean(this.selectedMasterShelf)
        },
        isInheritedShelf() {
            const source = String((this.effectivePolicy && this.effectivePolicy.source) || "")
            return Boolean(
                this.isVariation &&
                    this.effectiveMethod === "SHELF" &&
                    (source.includes("MASTER_SHELF") || this.masterShelfActive)
            )
        },
        pricingSourceDescription() {
            if (this.isInheritedShelf) {
                return `${this.masterName} supplies the live price for this variation.`
            }
            if (this.explicitItemPolicy) {
                return `This ${this.isVariation ? "variation" : "product"} has an explicit ${
                    this.effectiveMethod || "pricing"
                } rule.`
            }
            return "The legacy Standard price remains the effective base price."
        },
        policyVersions() {
            const loadedVersions = (this.policyContext && this.policyContext.versions) || {}
            const groupVersions = { ...(loadedVersions.customer_group_prices || {}) }
            this.customerGroupPrices.forEach((item) => {
                const groupId = String(item.customer_group_id || "")
                if (groupId && !Object.prototype.hasOwnProperty.call(groupVersions, groupId)) {
                    groupVersions[groupId] = null
                }
            })
            return {
                item_policy: Object.prototype.hasOwnProperty.call(loadedVersions, "item_policy")
                    ? loadedVersions.item_policy
                    : null,
                master_shelf: Object.prototype.hasOwnProperty.call(loadedVersions, "master_shelf")
                    ? loadedVersions.master_shelf
                    : null,
                customer_group_prices: groupVersions,
            }
        },
        effectiveFixedPrice() {
            const policyPrice = this.policyFixedPrice(this.effectivePolicy)
            if (policyPrice !== null) return policyPrice
            return finiteNumber(this.regularPrice)
        },
        validSalePrices() {
            return this.salePrices.filter((item) => this.isCompleteSalePrice(item))
        },
        tierSummaries() {
            if (this.pricingMethod !== "TIERED" && this.effectiveMethod !== "TIERED") return []
            const parsedTiers = this.tiers.map((tier) => ({
                minQuantity: finiteNumber(tier.min_quantity),
                unitPrice: finiteNumber(tier.unit_price),
            }))
            if (
                parsedTiers.some(
                    (tier, index) =>
                        tier.minQuantity === null ||
                        !Number.isInteger(tier.minQuantity) ||
                        tier.minQuantity < 1 ||
                        tier.unitPrice === null ||
                        tier.unitPrice <= 0 ||
                        (index > 0 && tier.minQuantity <= parsedTiers[index - 1].minQuantity)
                )
            ) {
                return []
            }
            return parsedTiers.map((tier, index) => {
                const next = parsedTiers[index + 1]
                return {
                    range: next ? `${tier.minQuantity}–${next.minQuantity - 1}` : `${tier.minQuantity}+`,
                    unitPrice: tier.unitPrice,
                }
            })
        },
        affectedVariations() {
            const context = this.policyContext || {}
            const candidates =
                context.affected_variations ||
                context.variations ||
                this.variations ||
                this.contextMaster.variations ||
                []
            return Array.isArray(candidates) ? candidates : []
        },
        affectedVariationCount() {
            const context = this.policyContext || {}
            const count =
                context.affected_variation_count !== undefined
                    ? context.affected_variation_count
                    : this.contextMaster.variation_count
            if (finiteNumber(count) !== null) return Number(count)
            return this.affectedVariations.length ? this.affectedVariations.length : null
        },
        hasUnusedCustomerGroup() {
            const usedIds = new Set(this.customerGroupPrices.map((item) => String(item.customer_group_id)))
            return this.customerGroups.some(
                (group) => this.isCustomerGroupActive(group) && !usedIds.has(String(group.id))
            )
        },
    },
    async mounted() {
        if (!this.canViewPricing || !this.item_code) return
        await Promise.all([this.fetchPrices(), this.loadCustomerGroups()])
    },
    methods: {
        normalizeMethod(value) {
            const method = String(value || "STANDARD").toUpperCase()
            return ["STANDARD", "TIERED", "SHELF"].includes(method) ? method : "STANDARD"
        },
        policyFixedPrice(policy) {
            if (!policy) return null
            const value = hasValue(policy.fixed_unit_price)
                ? policy.fixed_unit_price
                : hasValue(policy.unit_price)
                ? policy.unit_price
                : hasValue(policy.base_unit_price)
                ? policy.base_unit_price
                : policy.price
            return finiteNumber(value)
        },
        formatPrice(value) {
            const number = finiteNumber(value)
            return number === null ? "—" : `$${number.toFixed(2)}`
        },
        pricingPolicyUrl() {
            return `/pricing-policies/context/${encodeURIComponent(this.item_code)}?warehouse=${encodeURIComponent(
                this.warehouse || ""
            )}`
        },
        legacyPriceUrl() {
            return `/item-prices/${encodeURIComponent(this.item_code)}?company_level=${
                this.isLocationScope ? "false" : "true"
            }&warehouse=${encodeURIComponent(this.warehouse || "")}`
        },
        updateResult(priceResult = {}) {
            const regularPrice =
                priceResult.regularPrice ||
                priceResult.regular_price ||
                (hasValue(priceResult.unit_price) ? priceResult.unit_price : null)
            const regularPriceValue =
                regularPrice && typeof regularPrice === "object"
                    ? regularPrice.price_list_rate
                    : regularPrice
            const costPriceValue =
                regularPrice && typeof regularPrice === "object"
                    ? regularPrice.cost_price_list_rate
                    : priceResult.latest_cost_price

            if (hasValue(regularPriceValue)) this.regularPrice = regularPriceValue
            this.latestCostPrice = finiteNumber(costPriceValue)

            const hasSalePrices =
                Object.prototype.hasOwnProperty.call(priceResult, "salePrices") ||
                Object.prototype.hasOwnProperty.call(priceResult, "sale_prices")
            if (hasSalePrices) {
                const salePrices = priceResult.salePrices || priceResult.sale_prices || []
                this.salePrices = Array.isArray(salePrices)
                    ? salePrices.map((item) => ({
                          price_list_rate: item.price_list_rate,
                          valid_from: item.valid_from || "",
                          valid_upto: item.valid_upto || "",
                      }))
                    : []
            }
        },
        applyPricingContext(result = {}) {
            this.policyContext = result
            this.explicitItemPolicy = result.explicit_item_policy || result.explicitItemPolicy || null
            this.masterShelfPolicy =
                result.master_shelf || result.master_shelf_policy || result.masterShelfPolicy || null
            this.effectivePolicy =
                result.effective_base_policy ||
                result.effective_policy ||
                result.effectivePolicy ||
                this.explicitItemPolicy ||
                this.masterShelfPolicy ||
                { method: "STANDARD" }

            const legacyStandard =
                result.legacy_standard_fallback || result.legacy_standard || result.legacyStandard || result
            this.updateResult(legacyStandard)
            this.resetPolicyDraft()
        },
        resetPolicyDraft() {
            const policy = this.explicitItemPolicy || this.effectivePolicy || { method: "STANDARD" }
            this.pricingMethod = this.normalizeMethod(policy.method)

            const policyPrice = this.policyFixedPrice(policy)
            if (this.pricingMethod === "STANDARD") {
                if (policyPrice !== null) this.regularPrice = policyPrice
                this.fixedUnitPrice = this.regularPrice
            } else if (this.pricingMethod === "SHELF") {
                const shelfPrice = this.policyFixedPrice(this.masterShelfPolicy || policy)
                this.fixedUnitPrice = shelfPrice === null ? this.regularPrice : shelfPrice
            } else {
                this.fixedUnitPrice = policyPrice === null ? this.regularPrice : policyPrice
            }

            const policyTiers = Array.isArray(policy.tiers) ? policy.tiers : []
            this.tiers = policyTiers.length
                ? policyTiers.map((tier) => ({
                      min_quantity: tier.min_quantity,
                      unit_price: tier.unit_price,
                  }))
                : [{ min_quantity: 1, unit_price: "" }]
            if (this.tiers.length) this.tiers[0].min_quantity = 1

            const groupPrices =
                (this.policyContext &&
                    (this.policyContext.customer_group_prices || this.policyContext.customerGroupPrices)) ||
                []
            this.customerGroupPrices = Array.isArray(groupPrices)
                ? groupPrices.map((item) => ({
                      customer_group_id: item.customer_group_id || item.customerGroupId || "",
                      customer_group_name:
                          item.customer_group_name || item.customerGroupName || item.name || "",
                      unit_price: hasValue(item.fixed_unit_price) ? item.fixed_unit_price : item.unit_price,
                  }))
                : []
            this.validationErrors = emptyValidationErrors()
        },
        async fetchPrices() {
            const requestGeneration = ++this.pricingRequestGeneration
            this.loading = true
            this.contextError = ""
            this.legacyWarning = ""
            this.legacyPricesLoaded = false

            const policyRequest = http.get(this.pricingPolicyUrl())
            const legacyRequest = http
                .get(this.legacyPriceUrl())
                .then((response) => ({ response, error: null }))
                .catch((error) => ({ response: null, error }))

            try {
                const response = await policyRequest
                if (requestGeneration !== this.pricingRequestGeneration) return
                this.applyPricingContext(response.data.result || {})
            } catch (error) {
                if (requestGeneration !== this.pricingRequestGeneration) return
                const legacyResult = await legacyRequest
                if (requestGeneration !== this.pricingRequestGeneration) return
                if (legacyResult.response) {
                    const legacyResponse = legacyResult.response
                    this.applyPricingContext({
                        effective_policy: { method: "STANDARD" },
                        legacy_standard: legacyResponse.data.result,
                    })
                    this.legacyPricesLoaded = true
                    this.contextError =
                        "Advanced pricing context is unavailable. Legacy Standard pricing is shown read-only until the service is restored."
                } else {
                    this.contextError = errMsg(legacyResult.error, "Unable to load pricing")
                    this.policyContext = null
                }
                if (requestGeneration === this.pricingRequestGeneration) this.loading = false
                return
            }

            const legacyResult = await legacyRequest
            if (requestGeneration !== this.pricingRequestGeneration) return
            if (legacyResult.response) {
                const legacyResponse = legacyResult.response
                this.updateResult(legacyResponse.data.result || {})
                // Legacy rows supply the sale calendar/cost, not an override of
                // the authoritative policy price shown by the Standard editor.
                this.resetPolicyDraft()
                this.legacyPricesLoaded = true
            } else {
                this.legacyWarning =
                    "The dated sale-price schedule could not be loaded. Standard pricing cannot be saved until it is restored."
            }
            if (requestGeneration === this.pricingRequestGeneration) this.loading = false
        },
        async loadCustomerGroups() {
            if (!this.isLocationScope || !this.canViewPricing) return

            const storeGroups = this.miscState.customerGroups || []
            if (storeGroups.length) {
                this.customerGroups = storeGroups.slice()
                return
            }

            try {
                const response = await http.get("/customer-groups", {
                    params: { start: 0, limit: 100 },
                })
                this.customerGroups = Array.isArray(response.data.result) ? response.data.result : []
            } catch (error) {
                console.log("error in fetching customer groups", error)
            }
        },
        startEditing(method) {
            if (!this.canEditPricing || this.contextError) return
            this.resetPolicyDraft()
            this.editMode = true
            if (method) this.selectMethod(method)
        },
        cancelEditing() {
            this.editMode = false
            this.resetPolicyDraft()
        },
        selectMethod(method) {
            const normalizedMethod = this.normalizeMethod(method)
            if (this.isMethodUnavailable(normalizedMethod)) return
            this.pricingMethod = normalizedMethod
            this.validationErrors = emptyValidationErrors()
            if (normalizedMethod === "TIERED" && !this.tiers.length) {
                this.tiers = [{ min_quantity: 1, unit_price: "" }]
            }
            if (normalizedMethod === "SHELF" && !this.isVariation && this.masterShelfPrice !== null) {
                this.fixedUnitPrice = this.masterShelfPrice
            }
        },
        isMethodUnavailable(method) {
            return method === "SHELF" && this.isVariation && !this.masterShelfActive
        },
        hasPriceValue(value) {
            return hasValue(value)
        },
        isCompleteSalePrice(item) {
            return Boolean(
                hasValue(item.price_list_rate) && (item.valid_from || item.valid_upto)
            )
        },
        addSalePrice() {
            this.salePrices.push({
                price_list_rate: "",
                valid_from: "",
                valid_upto: "",
            })
        },
        removeSalePrice(index) {
            this.salePrices.splice(index, 1)
        },
        addTier() {
            const lastTier = this.tiers[this.tiers.length - 1]
            const lastMinimum = lastTier ? finiteNumber(lastTier.min_quantity) : null
            this.tiers.push({
                min_quantity: lastMinimum === null ? "" : lastMinimum + 1,
                unit_price: "",
            })
        },
        removeTier(index) {
            if (this.tiers.length === 1) return
            this.tiers.splice(index, 1)
            this.tiers[0].min_quantity = 1
        },
        customerGroupName(group) {
            return group.customer_group_name || group.name || group.title || group.id
        },
        isCustomerGroupActive(group) {
            return !group || !Object.prototype.hasOwnProperty.call(group, "status")
                ? true
                : group.status !== false && group.status !== 0 && group.status !== "0"
        },
        customerGroupOptionLabel(group) {
            const label = this.customerGroupName(group)
            return this.isCustomerGroupActive(group) ? label : `${label} (inactive)`
        },
        customerGroupOptions(groupPrice) {
            const selectedId = String(groupPrice.customer_group_id || "")
            const options = this.customerGroups.filter(
                (group) =>
                    this.isCustomerGroupActive(group) || String(group.id) === selectedId
            )
            if (selectedId && !options.some((group) => String(group.id) === selectedId)) {
                options.push({
                    id: groupPrice.customer_group_id,
                    name: groupPrice.customer_group_name || "Inactive customer group",
                    status: false,
                })
            }
            return options
        },
        groupPriceName(groupPrice) {
            const group = this.customerGroups.find(
                (item) => String(item.id) === String(groupPrice.customer_group_id)
            )
            return group ? this.customerGroupName(group) : groupPrice.customer_group_name || "Customer group"
        },
        isCustomerGroupUsed(groupId, currentIndex) {
            return this.customerGroupPrices.some(
                (item, index) => index !== currentIndex && String(item.customer_group_id) === String(groupId)
            )
        },
        addCustomerGroupPrice() {
            if (!this.isLocationScope) return
            const availableGroup = this.customerGroups.find(
                (group) =>
                    this.isCustomerGroupActive(group) &&
                    !this.customerGroupPrices.some(
                        (item) => String(item.customer_group_id) === String(group.id)
                    )
            )
            if (!availableGroup) return
            this.customerGroupPrices.push({
                customer_group_id: availableGroup.id,
                unit_price: "",
            })
        },
        removeCustomerGroupPrice(index) {
            this.customerGroupPrices.splice(index, 1)
        },
        validateSalePrices() {
            for (const salePrice of this.salePrices) {
                const allPresent = this.isCompleteSalePrice(salePrice)
                const somePresent = Boolean(
                    hasValue(salePrice.price_list_rate) || salePrice.valid_from || salePrice.valid_upto
                )
                if (!allPresent && somePresent) {
                    return "Enter a price and at least one start or end date for every dated sale."
                }
                if (allPresent && (finiteNumber(salePrice.price_list_rate) === null || Number(salePrice.price_list_rate) < 0)) {
                    return "Dated sale prices must be zero or greater."
                }
                if (
                    allPresent &&
                    salePrice.valid_from &&
                    salePrice.valid_upto &&
                    salePrice.valid_from > salePrice.valid_upto
                ) {
                    return "A dated sale cannot end before it starts."
                }
            }
            return ""
        },
        validateTiers() {
            const errors = []
            if (!this.tiers.length) return ["Add at least one Tiered breakpoint."]

            const minimums = []
            this.tiers.forEach((tier, index) => {
                const minimum = finiteNumber(tier.min_quantity)
                const price = finiteNumber(tier.unit_price)
                if (minimum === null || !Number.isInteger(minimum) || minimum < 1) {
                    errors.push(`Breakpoint ${index + 1} needs a positive whole-number quantity.`)
                }
                if (price === null || price <= 0) {
                    errors.push(`Breakpoint ${index + 1} needs a positive unit price.`)
                }
                minimums.push(minimum)
            })

            if (minimums[0] !== 1) errors.push("The first Tiered breakpoint must start at quantity 1.")
            const validMinimums = minimums.filter((value) => value !== null)
            if (new Set(validMinimums).size !== validMinimums.length) {
                errors.push("Tiered minimum quantities must be unique.")
            }
            for (let index = 1; index < minimums.length; index += 1) {
                if (
                    minimums[index - 1] !== null &&
                    minimums[index] !== null &&
                    minimums[index] <= minimums[index - 1]
                ) {
                    errors.push("Tiered minimum quantities must be in ascending order.")
                    break
                }
            }
            return Array.from(new Set(errors))
        },
        validateCustomerGroupPrices() {
            if (!this.isLocationScope) return []
            const errors = []
            const ids = []

            this.customerGroupPrices.forEach((item, index) => {
                if (!item.customer_group_id) {
                    errors.push(`Customer-group row ${index + 1} needs a group.`)
                } else {
                    ids.push(String(item.customer_group_id))
                }
                const price = finiteNumber(item.unit_price)
                if (price === null || price <= 0) {
                    errors.push(`Customer-group row ${index + 1} needs a positive absolute unit price.`)
                }
                const group = this.customerGroups.find(
                    (candidate) => String(candidate.id) === String(item.customer_group_id)
                )
                if (group && !this.isCustomerGroupActive(group)) {
                    const loadedRows =
                        (this.policyContext &&
                            (this.policyContext.customer_group_prices ||
                                this.policyContext.customerGroupPrices)) ||
                        []
                    const loaded = loadedRows.find(
                        (candidate) =>
                            String(candidate.customer_group_id || candidate.customerGroupId) ===
                            String(item.customer_group_id)
                    )
                    const loadedPrice = loaded
                        ? finiteNumber(
                              hasValue(loaded.fixed_unit_price)
                                  ? loaded.fixed_unit_price
                                  : loaded.unit_price
                          )
                        : null
                    if (!loaded || price !== loadedPrice) {
                        errors.push(
                            `${this.customerGroupName(group)} is inactive. Keep its existing price unchanged or remove the row.`
                        )
                    }
                }
            })

            if (new Set(ids).size !== ids.length) {
                errors.push("Each customer group can have only one location price.")
            }
            return errors
        },
        validatePricingDraft() {
            const errors = emptyValidationErrors()

            if (this.pricingMethod === "STANDARD") {
                const regularPrice = finiteNumber(this.regularPrice)
                if (!this.legacyPricesLoaded) {
                    errors.general = "Reload the dated sale-price schedule before saving Standard pricing."
                } else if (regularPrice === null || regularPrice < 0) {
                    errors.general = "Regular price must be zero or greater."
                } else {
                    errors.general = this.validateSalePrices()
                }
            } else if (this.pricingMethod === "TIERED") {
                errors.tiers = this.validateTiers()
            } else if (this.isVariation && !this.masterShelfActive) {
                errors.general = `Enable Shelf pricing on ${this.masterName} before inheriting it here.`
            } else if (!this.isVariation) {
                const shelfPrice = finiteNumber(this.fixedUnitPrice)
                if (shelfPrice === null || shelfPrice < 0) {
                    errors.general = "Shelf price must be zero or greater."
                }
            }

            errors.customerGroups = this.validateCustomerGroupPrices()
            this.validationErrors = errors
            return !errors.general && !errors.tiers.length && !errors.customerGroups.length
        },
        buildPolicyPayload() {
            const payload = {
                warehouse_id: this.isLocationScope ? this.warehouse : null,
                method: this.pricingMethod,
                customer_group_prices: this.isLocationScope
                    ? this.customerGroupPrices.map((item) => ({
                          customer_group_id: item.customer_group_id,
                          fixed_unit_price: Number(item.unit_price),
                      }))
                    : [],
                expected_versions: this.policyVersions,
            }

            if (this.pricingMethod === "STANDARD") {
                payload.fixed_unit_price = Number(this.regularPrice)
                payload.sale_prices = this.salePrices
                    .filter((item) => this.isCompleteSalePrice(item))
                    .map((item) => ({
                        price_list_rate: Number(item.price_list_rate),
                        valid_from: item.valid_from,
                        valid_upto: item.valid_upto,
                    }))
            } else if (this.pricingMethod === "TIERED") {
                payload.tiers = this.tiers.map((tier) => ({
                    min_quantity: Number(tier.min_quantity),
                    unit_price: Number(tier.unit_price),
                }))
                payload.fixed_unit_price = Number(this.tiers[0].unit_price)
            } else if (!this.isVariation) {
                payload.fixed_unit_price = Number(this.fixedUnitPrice)
            } else {
                payload.fixed_unit_price = Number(this.masterShelfPrice)
            }
            return payload
        },
        firstValidationError() {
            return (
                this.validationErrors.general ||
                this.validationErrors.tiers[0] ||
                this.validationErrors.customerGroups[0] ||
                "Review the pricing form before saving."
            )
        },
        isVersionConflict(error) {
            return Boolean(
                error &&
                    error.response &&
                    error.response.status === 409 &&
                    error.response.data &&
                    error.response.data.error &&
                    error.response.data.error.code === "PRICING_VERSION_CONFLICT"
            )
        },
        async refreshAfterVersionConflict() {
            this.editMode = false
            await this.fetchPrices()
            this.$toast.error("Pricing changed in another session. The latest values have been reloaded.")
        },
        async handleSavePrice() {
            if (this.processing || !this.canEditPricing) return
            if (!this.validatePricingDraft()) {
                this.$toast.error(this.firstValidationError())
                return
            }

            this.processing = true
            this.$emit("processing-change", true)
            const requestGeneration = ++this.pricingRequestGeneration
            const scopeAtSave = `${this.item_code}:${this.warehouse || ""}`
            try {
                const policyResponse = await http.put(this.pricingPolicyUrl(), this.buildPolicyPayload())

                if (
                    scopeAtSave !== `${this.item_code}:${this.warehouse || ""}` ||
                    requestGeneration !== this.pricingRequestGeneration
                ) {
                    return
                }

                if (policyResponse.data && policyResponse.data.result) {
                    this.applyPricingContext(policyResponse.data.result)
                } else {
                    await this.fetchPrices()
                }
                this.$toast.success("Pricing updated successfully!")
                this.editMode = false
            } catch (error) {
                if (
                    scopeAtSave !== `${this.item_code}:${this.warehouse || ""}` ||
                    requestGeneration !== this.pricingRequestGeneration
                ) {
                    return
                }
                console.log("error in saving price", error)
                if (this.isVersionConflict(error)) {
                    await this.refreshAfterVersionConflict()
                } else {
                    this.$toast.error(errMsg(error, "Error in saving price"))
                }
            } finally {
                this.processing = false
                this.$emit("processing-change", false)
            }
        },
        async performPolicyAction(action, successMessage) {
            if (this.processing || !this.canEditPricing) return
            const targetItemCode = action === "DISABLE_SHELF" && this.isVariation
                ? this.contextMaster.item_code
                : this.item_code
            if (!targetItemCode) {
                this.$toast.error("Open the master product to disable its Shelf pricing.")
                return
            }
            this.processing = true
            this.$emit("processing-change", true)
            const requestGeneration = ++this.pricingRequestGeneration
            const scopeAtAction = `${this.item_code}:${this.warehouse || ""}`
            const actionUrl = `/pricing-policies/context/${encodeURIComponent(targetItemCode)}`
            const actionPayload = {
                warehouse_id: this.isLocationScope ? this.warehouse : null,
                action,
                expected_versions: this.policyVersions,
            }
            try {
                const response = await http.delete(actionUrl, { data: actionPayload })
                if (
                    scopeAtAction !== `${this.item_code}:${this.warehouse || ""}` ||
                    requestGeneration !== this.pricingRequestGeneration
                ) {
                    return
                }
                if (targetItemCode !== this.item_code) {
                    await this.fetchPrices()
                } else if (response.data && response.data.result) {
                    this.applyPricingContext(response.data.result)
                } else {
                    await this.fetchPrices()
                }
                this.editMode = false
                this.$toast.success(successMessage)
            } catch (error) {
                if (
                    scopeAtAction !== `${this.item_code}:${this.warehouse || ""}` ||
                    requestGeneration !== this.pricingRequestGeneration
                ) {
                    return
                }
                if (this.isVersionConflict(error)) {
                    await this.refreshAfterVersionConflict()
                } else {
                    this.$toast.error(errMsg(error, "Unable to update pricing policy"))
                }
            } finally {
                this.processing = false
                this.$emit("processing-change", false)
            }
        },
        async removeExplicitOverride() {
            if (!this.isVariation || !this.selectedItemPolicy) return
            await this.performPolicyAction("REMOVE_OVERRIDE", "Variation override removed.")
        },
        async disableShelf() {
            if (!this.canDisableShelfAtScope) return
            const confirmed = window.confirm(
                `Disable the live Shelf price on ${this.masterName}? Variations without overrides will return to Standard pricing.`
            )
            if (!confirmed) return
            await this.performPolicyAction("DISABLE_SHELF", "Master Shelf pricing disabled.")
        },
    },
    watch: {
        warehouse() {
            if (!this.canViewPricing || !this.item_code) return
            this.editMode = false
            this.fetchPrices()
            this.loadCustomerGroups()
        },
        item_code() {
            if (!this.canViewPricing || !this.item_code) return
            this.editMode = false
            this.fetchPrices()
        },
    },
}
</script>

<style lang="scss" scoped>
@use "sass:color";
@import "../../assets/variables.scss";
@import "../../assets/mixins.scss";

.pricing-workspace {
    min-width: 0;
    color: $textColor;
}

.workspace-toolbar,
.section-heading,
.sale-price-heading,
.form-actions,
.inline-actions {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 12px;
}

.workspace-toolbar {
    padding-bottom: 16px;
}

.workspace-heading,
.section-heading > div,
.sale-price-heading > div {
    min-width: 0;
}

.eyebrow {
    color: rgba($textColor, 0.65);
    font-size: 0.72rem;
    font-weight: 700;
    letter-spacing: 0.08em;
    text-transform: uppercase;
}

.method-title-row {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 8px;
    margin-top: 3px;
}

h3,
h4,
h5,
p {
    margin-top: 0;
}

h3 {
    margin-bottom: 0;
    font-size: 1.35rem;
}

h4 {
    margin-bottom: 4px;
    font-size: 1rem;
}

h5 {
    margin-bottom: 3px;
    font-size: 0.92rem;
}

.workspace-subtitle,
.section-heading p,
.sale-price-heading p,
.section-help,
.inheritance-panel p,
.shelf-impact-panel p {
    margin-bottom: 0;
    color: rgba($textColor, 0.72);
    line-height: 1.5;
}

.source-badge {
    display: inline-flex;
    align-items: center;
    min-height: 24px;
    padding: 3px 8px;
    border: 1px solid rgba($primaryColor, 0.25);
    border-radius: 999px;
    background: rgba($primaryColor, 0.06);
    color: $primaryColor;
    font-size: 0.75rem;
    font-weight: 700;
}

.source-badge.inherited {
    border-color: rgba($secondaryColor, 0.45);
    background: rgba($secondaryColor, 0.12);
    color: color.adjust($secondaryColor, $lightness: -18%);
}

.loading-message,
.permission-message,
.context-warning,
.scope-note,
.empty-note,
.form-error {
    padding: 12px;
    border-radius: 8px;
}

.loading-message,
.scope-note,
.empty-note {
    background: $lightHoverColor;
}

.permission-message,
.form-error {
    border: 1px solid rgba($dangerColor, 0.35);
    background: rgba($dangerColor, 0.08);
    color: color.adjust($dangerColor, $lightness: -8%);
}

.context-warning {
    margin-bottom: 16px;
    border: 1px solid rgba($warningColor, 0.55);
    background: rgba($warningColor, 0.12);
}

.inheritance-panel,
.shelf-impact-panel,
.tier-summary {
    padding: 14px;
    border: 1px solid rgba($secondaryColor, 0.42);
    border-radius: 10px;
    background: rgba($secondaryColor, 0.08);
}

.inheritance-panel {
    display: flex;
    justify-content: space-between;
    gap: 16px;
    margin-bottom: 16px;
}

.inline-actions {
    justify-content: flex-end;
    flex-wrap: wrap;
}

.scope-action-note {
    align-self: center;
    color: rgba($textColor, 0.68);
    font-size: 0.78rem;
}

.policy-form {
    display: grid;
    gap: 16px;
}

.method-fieldset,
.editor-section {
    min-width: 0;
    margin: 0;
    padding: 16px;
    border: 1px solid $lightBorderColor;
    border-radius: 10px;
}

.method-fieldset legend {
    width: auto;
    margin: 0;
    padding: 0 4px;
    font-size: 1rem;
    font-weight: 700;
}

.method-grid {
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: 10px;
    margin-top: 12px;
}

.method-option {
    display: flex;
    align-items: flex-start;
    min-width: 0;
    gap: 10px;
    margin: 0;
    padding: 13px;
    border: 1px solid $lightBorderColor;
    border-radius: 9px;
    background: $lightCardBg;
    cursor: pointer;
    transition: border-color 0.15s, background-color 0.15s, box-shadow 0.15s;
}

.method-option:hover {
    border-color: rgba($primaryColor, 0.55);
}

.method-option.selected {
    border-color: $primaryColor;
    background: rgba($primaryColor, 0.055);
    box-shadow: 0 0 0 1px rgba($primaryColor, 0.08);
}

.method-option:focus-within {
    @include focus-ring;
}

.method-option.unavailable {
    cursor: not-allowed;
    opacity: 0.55;
}

.method-option input {
    flex: 0 0 auto;
    margin-top: 3px;
    accent-color: $primaryColor;
}

.method-copy {
    display: grid;
    min-width: 0;
    gap: 4px;
}

.method-copy small {
    color: rgba($textColor, 0.68);
    line-height: 1.4;
}

.shelf-unavailable-help {
    margin-top: 10px;
}

.editor-section {
    display: grid;
    gap: 14px;
}

.field-control {
    display: grid;
    min-width: 0;
    gap: 5px;
}

.field-control label {
    margin: 0;
    font-size: 0.78rem;
    font-weight: 700;
}

.field-control input,
.field-control select {
    width: 100%;
    min-width: 0;
    min-height: 40px;
    padding: 8px 10px;
    border: 1px solid $lightBorderColor;
    border-radius: 6px;
    background: $lightCardBg;
    color: $textColor;
}

.field-control input:focus,
.field-control select:focus {
    border-color: $primaryColor;
    @include focus-ring;
}

.field-control input:disabled {
    background: $lightHoverColor;
    color: rgba($textColor, 0.7);
}

.regular-price-control,
.shelf-price-control {
    max-width: 320px;
}

.repeatable-list {
    display: grid;
    min-width: 0;
    gap: 9px;
}

.sale-price-row,
.tier-row,
.customer-group-row {
    display: grid;
    align-items: end;
    min-width: 0;
    gap: 10px;
    padding: 11px;
    border: 1px solid $lightBorderColor;
    border-radius: 8px;
    background: $lightHoverColor;
}

.sale-price-row {
    grid-template-columns: minmax(110px, 0.75fr) minmax(140px, 1fr) minmax(140px, 1fr) 38px;
}

.tier-row {
    grid-template-columns: 30px minmax(130px, 1fr) minmax(130px, 1fr) 38px;
}

.customer-group-row {
    grid-template-columns: minmax(180px, 1.35fr) minmax(140px, 0.85fr) 38px;
}

.tier-index {
    align-self: center;
    color: rgba($textColor, 0.62);
    font-size: 0.78rem;
    font-weight: 800;
    text-align: center;
}

.primary-action,
.secondary-action,
.text-action,
.danger-text-action,
.icon-action {
    border: 0;
    border-radius: 6px;
    font: inherit;
    cursor: pointer;
}

.primary-action,
.secondary-action {
    min-height: 40px;
    padding: 8px 14px;
    font-weight: 700;
}

.primary-action {
    background: $primaryColor;
    color: white;
}

.secondary-action {
    border: 1px solid $lightBorderColor;
    background: $lightCardBg;
    color: $primaryColor;
}

.text-action,
.danger-text-action {
    padding: 5px 7px;
    background: transparent;
    color: $primaryColor;
    font-weight: 700;
    text-align: left;
}

.danger-text-action {
    color: $dangerColor;
}

.icon-action {
    display: inline-grid;
    place-items: center;
    width: 38px;
    height: 38px;
    border: 1px solid $lightBorderColor;
    background: $lightCardBg;
    color: $dangerColor;
}

button:focus-visible,
input:focus-visible,
select:focus-visible,
a:focus-visible {
    @include focus-ring;
}

button:disabled {
    cursor: not-allowed;
    opacity: 0.55;
}

.validation-list {
    margin: 0;
    padding: 10px 10px 10px 30px;
    border: 1px solid rgba($dangerColor, 0.35);
    border-radius: 8px;
    background: rgba($dangerColor, 0.07);
    color: color.adjust($dangerColor, $lightness: -8%);
}

.tier-summary-list {
    display: flex;
    flex-wrap: wrap;
    gap: 7px;
    margin-top: 7px;
}

.tier-summary-list span {
    padding: 5px 8px;
    border-radius: 999px;
    background: $lightCardBg;
    font-size: 0.82rem;
}

.shelf-impact-panel.warning {
    border-color: rgba($warningColor, 0.6);
    background: rgba($warningColor, 0.1);
}

.impact-price {
    margin-top: 8px;
    font-weight: 700;
}

.variation-list {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 4px 16px;
    margin: 10px 0 0;
    padding-left: 18px;
}

.policy-summary {
    display: grid;
    gap: 18px;
}

.summary-grid {
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: 10px;
    margin: 0;
}

.summary-grid > div {
    padding: 12px;
    border: 1px solid $lightBorderColor;
    border-radius: 8px;
}

.summary-grid dt {
    color: rgba($textColor, 0.65);
    font-size: 0.74rem;
    font-weight: 700;
    text-transform: uppercase;
}

.summary-grid dd {
    margin: 4px 0 0;
    font-size: 1rem;
    font-weight: 700;
}

.summary-section {
    min-width: 0;
    padding-top: 14px;
    border-top: 1px solid $lightBorderColor;
}

.responsive-table-wrap {
    max-width: 100%;
    overflow-x: auto;
}

table {
    width: 100%;
    border-collapse: collapse;
}

th,
td {
    padding: 9px 10px;
    border-bottom: 1px solid $lightBorderColor;
    text-align: left;
    white-space: nowrap;
}

.group-price-summary {
    display: grid;
    gap: 6px;
    margin: 10px 0 0;
    padding: 0;
    list-style: none;
}

.group-price-summary li {
    display: flex;
    justify-content: space-between;
    gap: 12px;
    padding: 9px 0;
    border-bottom: 1px solid $lightBorderColor;
}

.summary-actions {
    display: flex;
    justify-content: flex-end;
}

.form-actions {
    justify-content: flex-end;
}

.detail-link {
    margin-top: 16px;
}

.detail-link a {
    color: $primaryColor;
    font-weight: 700;
}

.pricing-workspace.dark {
    color: $darkTextColor;
}

.pricing-workspace.dark .eyebrow,
.pricing-workspace.dark .workspace-subtitle,
.pricing-workspace.dark .section-heading p,
.pricing-workspace.dark .sale-price-heading p,
.pricing-workspace.dark .section-help,
.pricing-workspace.dark .inheritance-panel p,
.pricing-workspace.dark .shelf-impact-panel p,
.pricing-workspace.dark .method-copy small,
.pricing-workspace.dark .summary-grid dt,
.pricing-workspace.dark .tier-index {
    color: rgba($darkTextColor, 0.72);
}

.pricing-workspace.dark .scope-action-note {
    color: rgba($darkTextColor, 0.7);
}

.pricing-workspace.dark .method-fieldset,
.pricing-workspace.dark .editor-section,
.pricing-workspace.dark .method-option,
.pricing-workspace.dark .sale-price-row,
.pricing-workspace.dark .tier-row,
.pricing-workspace.dark .customer-group-row,
.pricing-workspace.dark .summary-grid > div,
.pricing-workspace.dark .summary-section,
.pricing-workspace.dark th,
.pricing-workspace.dark td,
.pricing-workspace.dark .group-price-summary li {
    border-color: $darkBorderColor;
}

.pricing-workspace.dark .method-option,
.pricing-workspace.dark .field-control input,
.pricing-workspace.dark .field-control select,
.pricing-workspace.dark .secondary-action,
.pricing-workspace.dark .icon-action,
.pricing-workspace.dark .tier-summary-list span {
    background: $darkCardBg;
    color: $darkTextColor;
}

.pricing-workspace.dark .method-option.selected {
    border-color: $secondaryColorDark;
    background: rgba($secondaryColorDark, 0.1);
}

.pricing-workspace.dark .sale-price-row,
.pricing-workspace.dark .tier-row,
.pricing-workspace.dark .customer-group-row,
.pricing-workspace.dark .loading-message,
.pricing-workspace.dark .scope-note,
.pricing-workspace.dark .empty-note,
.pricing-workspace.dark .field-control input:disabled {
    background: color.adjust($darkCardBg, $lightness: 4%);
}

.pricing-workspace.dark .text-action,
.pricing-workspace.dark .detail-link a {
    color: $secondaryColorDark;
}

.pricing-workspace.dark .danger-text-action,
.pricing-workspace.dark .icon-action {
    color: #ffb4ab;
}

.pricing-workspace.dark .source-badge.inherited {
    color: color.adjust($secondaryColorDark, $lightness: 15%);
}

@media (max-width: 900px) {
    .method-grid {
        grid-template-columns: 1fr;
    }

    .sale-price-row {
        grid-template-columns: repeat(2, minmax(0, 1fr)) 38px;
    }

    .sale-price-row .field-control:first-child {
        grid-column: 1 / -2;
    }

    .summary-grid {
        grid-template-columns: repeat(2, minmax(0, 1fr));
    }
}

@media (max-width: 575px) {
    .workspace-toolbar,
    .section-heading,
    .sale-price-heading,
    .inheritance-panel {
        align-items: stretch;
        flex-direction: column;
    }

    .workspace-toolbar .secondary-action,
    .section-heading .text-action,
    .sale-price-heading .text-action {
        align-self: flex-start;
    }

    .method-fieldset,
    .editor-section {
        padding: 12px;
    }

    .sale-price-row,
    .tier-row,
    .customer-group-row {
        grid-template-columns: minmax(0, 1fr) 38px;
    }

    .sale-price-row .field-control,
    .sale-price-row .field-control:first-child,
    .tier-row .field-control,
    .customer-group-row .field-control {
        grid-column: 1;
    }

    .sale-price-row .icon-action,
    .tier-row .icon-action,
    .customer-group-row .icon-action {
        grid-column: 2;
        grid-row: 1;
    }

    .tier-index {
        display: none;
    }

    .summary-grid {
        grid-template-columns: 1fr;
    }

    .variation-list {
        grid-template-columns: 1fr;
    }

    .form-actions {
        display: grid;
        grid-template-columns: 1fr 1fr;
    }
}
</style>
